import {IReg, REG} from "/@lib@/commons/registry.js";
import {AccelKeyMgr, ACTION, IAction} from "/@lib@/commons/actions.js";
import {IActionable, IContextMenuActions, IContextMenuActionsPointer, IFocusActionables} from "/@back@/commons/actionables.js";
import {Grid, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {GridDataHolderBase, GridDataHolderJsonArray, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {GridColDef, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";
import {BASIS} from "/@back@/commons/basis.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {IInfo, IInfoBroker, IInfoConsumer} from "/@lib@/commons/infos.js";
import {GridArrayToTree, GridDataHolderJsonTree, IGridCategorizer} from "/@back@/commons/widgets/tree.js";
import {IDepotUiEnv, JStoreChange} from "/@lib@/store/depot.js";
import {JNodeShortProps, URLTREE} from "/@lib@/store/urlTree.js";
import {IDepotCtx, InfoCurrentRes, resPath} from "/@lib@/store/res.js";
import {CellBuilderResIconName, redrawResLine} from "/@back@/store/widgets/resGridColumns.js";


/**
 * store-res-grid : Widget des listes de ressources d'un dépot.
 */
export interface ResGrid extends Grid {
	initialize(init: OResGridInit): this;

	/** Fournisseur des donnée courant (srcGridDatas ou celui issu du categorizer). */
	get dataHolder(): GridDataHolderBase<JNodeShortProps>;
	set dataHolder(val: GridDataHolderBase<JNodeShortProps>);
}

export interface OResGridInit extends OGridSmallInit {
	reg?: IReg<IDepotUiEnv>


	/** InfoBroker destiné à dispatcher les events pour les actions de ce ResGrid. */
	infoBroker?: IInfoBroker

	/** Réagira aux infos issues de ce broker (res courante...) */
	resHandlingReact?: IInfoBroker

	/** Actions associées aux items sélectionnés (menu contextuel et IFocusActionables). */
	actions?: IAction<IResGridCtx>[]

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<IResGridCtx>

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<IResGridCtx>

	/** TODO item draggables. */
	draggable?: boolean

	/** Restreint l'affichage aux noeuds respectant ce filtre. */
	//srcFilter?: (data: JUrlTreeNodeShortProps) => boolean  NON inmplémenté!

	dataHolder?: IResGridDatas

	/**
	 * Si true le widget s'abonnera au connectedCallback aux events de modif et se désabonnera au disconnectedCallback.
	 * Sinon le widget ne s'abonnera pas (affichages éphémères ou en masse, gestion du refresh géré par l'appelant).
	 * **!! NON Codé !!**
	 */
	listenChanges?: boolean

	/** Supprime le lineDrawer par défaut (avec null) ou customise le lineDrawer. */
	lineDrawer?: null | IGridLineDrawer
}

export class ResGrid extends Grid implements IResGridCtx, IFocusActionables<IResGridCtx>, IContextMenuActionsPointer<IResGridCtx>, IGridLineDrawer, IInfoConsumer {

	reg: IReg<IDepotUiEnv>;

	config: OResGridInit;
	accelKeyMgr: AccelKeyMgr<IResGridCtx>;

	/** Mémoire des actions pour le menuContextuel. */
	actions?: IAction<IResGridCtx>[];

	/**
	 * Datas à plat, provider pour un Categorizer ou dataHolder direct pour ce grid.
	 * A utiliser pour les mises à jour des donnnées (et pas dataHolder).
	 */
	resGridDatas: IResGridDatas;

	/** API IResGridCtx */
	resList: JNodeShortProps[] = [];

	/** API IResGridCtx pour les actions */
	infoBroker: IInfoBroker;


	resHandling?: IInfoBroker

	protected _treeView: GridArrayToTree<JNodeShortProps, JNodeShortProps>;

	get categorizer(): IGridCategorizer<JNodeShortProps, JNodeShortProps> {
		return this._treeView ? this._treeView.categorizer : null;
	}

	setCategorizer(categorizer: IGridCategorizer<JNodeShortProps, JNodeShortProps>, target?: GridDataHolderJsonTree<JNodeShortProps>) {
		if (categorizer) {
			if (!this._treeView) this._treeView = new GridArrayToTree(target).setProvider(this.resGridDatas);
			this._treeView.setCategorizer(categorizer);
			this.dataHolder = this._treeView.target;
		} else if (this._treeView) {
			//On supprime le categorizer
			this._treeView.setProvider(null);
			this._treeView = null;
			this.dataHolder = this.resGridDatas;
		}
	}

	selectResPath(resPath: resPath): boolean {
		return this._selectRowKey(this.resGridDatas.findRowKeyByResPath(resPath));
	}

	_selectRowKey(rowKey: JNodeShortProps): boolean {
		if (!rowKey) return false;
		if (this._treeView && this._treeView.categorizer) {
			const id = this._treeView.categorizer.getId(rowKey);
			rowKey = this._treeView.getTargetEntryById(id);
		}
		const offset = this.dataHolder.getOffset(rowKey);
		if (offset < 0) return false;
		this.setSelectedRows(offset);
		return true;
	}


	protected _initialize(init: OResGridInit) {
		this.config = init || {};
		this.reg = this.findReg(init);
		if (!init.dataHolder) init.dataHolder = new ResGridDatas(/*this.reg, init.srcFilter*/).setDatas([]);
		if (!init.selType) init.selType = 'multi';
		if (!init.columnDefs) init.columnDefs = this.getDefaultColumnDefs();
		if (!init.defaultActionCtx) init.defaultActionCtx = this;
		this.infoBroker = init.infoBroker;
		this.resHandling = init.resHandlingReact;
		if (!('lineDrawer' in init)) init.lineDrawer = this;
		this.resGridDatas = init.dataHolder;

		super._initialize(init);

		this.addEventListener('grid-select', function (this: ResGrid, ev: Event) {
			this.resList = this.dataHolder.getSelectedDatas();
			//console.log("spaceTree.shortDescs::::", spaceTree.shortDescs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IResGridCtx>>('c-focus-actions', {detail: this, bubbles: true, composed: true}));
		});

		if (init.actions) this.actions = ACTION.injectSepByGroup(init.actions, this.reg.getPref("groupOrder.store.res", ""), this);
		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

		if (init.draggable) {
			console.log("TODO:::OResGridInit.draggable");
			//this.linesNode.setAttribute("draggable", "true");
			// this.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
			// 	//console.log(ev);
			// 	ITEM.setShortDescTransferToDragSession(DOMSH.findHost<ResGrid>(this), ev, 'build');
			// });
			// this.linesNode.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);
		}
	}

	getDefaultColumnDefs() {
		return [new GridColDef('resName')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('8em').setSortable(true)
			.setCellBuilder(new CellBuilderResIconName(this.reg, false))
		];
	}

	buildInitFromAtts(init?: any): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute("actions")) init.actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		return init;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.resHandling) this.resHandling.addConsumer(this);
		if (this.config.listenChanges && this.reg.env.depotEvents) {
			this.reg.env.depotEvents.on("resChange", (this._resChange = (m: JStoreChange) => {
				console.trace("TODO:::");
			}));
			this.reg.env.depotEvents.on("connOpened", (this._connOpened = () => {
				console.trace("TODO:::");
			}));
		}
	}

	protected _resChange: (m: JStoreChange) => void;
	protected _connOpened: () => void;

	disconnectedCallback() {
		if (this.resHandling) this.resHandling.removeConsumer(this);
		if (this.config.listenChanges && this.reg.env.depotEvents) {
			this.reg.env.depotEvents.removeListener("resChange", this._resChange);
			this._resChange = null;
			this.reg.env.depotEvents.removeListener("connOpened", this._connOpened);
			this._connOpened = null;
		}
	}

	protected _currentResPath: resPath;

	ensureRowVisibleByResPath(resPath: resPath): boolean {
		const rowKey = this.resGridDatas.findRowKeyByResPath(resPath);
		if (rowKey) this.ensureRowVisible(this.dataHolder.getOffset(rowKey));
		return rowKey != null;
	}

	setCurrentResPath(resPath: resPath | null) {
		if (this._currentResPath === resPath) return;
		this._currentResPath = resPath;
		this.invalidateRows();
		//if (this._currentResPath) this.ensureRowVisibleByResPath(this._currentResPath); //Ergonomiquement douteux : à rendre configuarble ?
	}

	redrawLine(row: GridDataRowJson<JNodeShortProps>, line: HTMLElement): void {
		redrawResLine(this.reg, row, line, this._currentResPath);
	}

	/** boutons à afficher dans la focusToolbar. */
	protected _focusActionables: IActionable<IResGridCtx>[];
	get focusActionables(): IActionable<IResGridCtx>[] {
		if (!this._focusActionables && this.actions) this._focusActionables = ActionBtn.buildButtons<IResGridCtx>(this.actions, this, 'bar');
		return this._focusActionables;
	};

	get ctxMenuActions(): IContextMenuActions<IResGridCtx> | null {return {actions: this.actions, actionContext: this, rect: this.getSelRect()}}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentRes) {
			this.setCurrentResPath(info.resPath);
		}
	}

	//*** API IResGridCtx ***

	get me() {return this}

	get emitter() {return this}
}

REG.reg.registerSkin('store-res-grid', 1, /* language=CSS */ `
	.current {
		background-position: right;
		background-repeat: no-repeat;
		background-image: var(--row-current-img-end);
		background-color: var(--row-current-bgcolor);
	}

	.unlisted > .cell {
		background-position: bottom .2em right .2em;
		background-repeat: no-repeat;
		background-size: .7em;
		background-image: url(/@skin@/store/states/limited.svg);
	}

	.trashed > .cell {
		background-position: bottom .2em right .2em;
		background-repeat: no-repeat;
		background-size: .7em;
		background-image: url(/@skin@/store/states/invisible.svg);
	}
`);

customElements.define('store-res-grid', ResGrid);


/**
 * Contexte pour les actions du ResGrid.
 * Abstraction de ResGrid pour être utilisé en contexte d'actions wrappées/surchagées.
 */
export interface IResGridCtx extends IDepotCtx {
	me: ResGrid
}

/** Api de la source de données pou un ResGrid. */
export interface IResGridDatas extends GridDataHolderJsonArray<JNodeShortProps> {
	findRowKeyByResPath(resPath: resPath): JNodeShortProps

	removeByResPath(resPath: resPath, anyLeafVersion:boolean): void

	refreshOrInsertNode(nodeProps: JNodeShortProps, anyLeafVersion:boolean): void

	refreshFields(rowKey: JNodeShortProps, newProps: JNodeShortProps): void
}


export class ResGridDatas extends GridDataHolderJsonArray<JNodeShortProps> implements IResGridDatas {

	constructor(/*reg: IReg<IDepotUiEnv>, srcFilter?: (data: JUrlTreeNodeShortProps) => boolean*/) {
		super();
	}

	findRowKeyByResPath(resPath: resPath): JNodeShortProps {
		for (const d of this._datas) if (d.permaPath === resPath) return d;
		return null;
	}

	removeByResPath(resPath: resPath, anyLeafVersion:boolean) {
		if(anyLeafVersion) resPath = URLTREE.extractUnversionedLeafPath(resPath);
		for (let i = this._datas.length - 1; i >= 0; i--) {
			if (URLTREE.isDescendantPathOrEqual(resPath, this._datas[i].permaPath, anyLeafVersion)) {
				this.updateDatas(i, 1);
			}
		}
	}

	refreshOrInsertNode(nodeProps: JNodeShortProps, anyLeafVersion:boolean) {
		const resPath = anyLeafVersion ? URLTREE.extractUnversionedLeafPath(nodeProps.permaPath) : nodeProps.permaPath;
		for (let i = 0; i < this._datas.length; i++) {
			if (URLTREE.isDescendantPathOrEqual(resPath, this._datas[i].permaPath, anyLeafVersion)) {
				this.updateDatas(i, 1, Object.assign(Object.create(null), nodeProps));
				return;
			}
		}
		//Pas trouvé on insert.
		const idx = this.findInsertPoint(this._datas, nodeProps);
		if (idx >= 0) this.updateDatas(idx, 0, Object.assign(Object.create(null), nodeProps));
	}

	refreshFields(rowKey: JNodeShortProps, newProps: JNodeShortProps) {
		const idx = this.getOffset(rowKey);
		if (idx >= 0) this.updateDatas(idx, 1, Object.assign(Object.create(null), newProps));
	}
}
